import { format } from 'date-fns';
import {
  type Interaction,
  type InteractionType,
} from '../types';
import { type TypeOptions, toast } from 'react-toastify';
import { getSlideUrl } from '@services/utils.service';
import { v4 } from 'uuid';
import CustomCloseButton from '../components/CustomCloseButton';

export const getInteractionNameFromType = (interactionType: InteractionType) => {
  switch (interactionType) {
    case 'pollWithOptions':
      return 'Power Polls';
    case 'freeForm':
      return 'Open-Ended Polls';
    case 'map':
      return 'Magic Maps';
    case 'wordCloud':
      return 'Wonder Words';
    case 'wheel':
      return 'Choice Circle';
    case 'emojisEverywhere':
      return 'Emojis Everywhere';
    case 'talkingTiles':
      return 'Talking Tiles';
    case 'transientThought':
      return 'Transient Thoughts';
    case 'newWheel':
      return 'Winning Wheel';
    case 'linkLibrary':
      return 'Link Library';
    case 'Quick Questions':
      return 'Quick Questions';
    case 'textTrack':
      return 'Text Track';
    case 'PDF Slide':
      return 'PDF Slide';
    case 'ratingPolls':
      return 'Rating Polls';
    case 'fantasticFans':
      return 'Fantastic Fans';
    case 'multipleChoice':
      return 'Multiple Choice';
    case 'quiz':
      return 'Quiz';
    case 'leaderboard':
      return 'Leaderboard';
    case 'credits':
      return 'End Credits';
    default:
      return '';
  }
};

export const getFormattedDate = (date?: string) => {
  if (!date) return '';
  return format(new Date(date), 'MMM dd, EEEE • hh:mm a');
};

export const notify = (message: string | React.ReactNode, type: TypeOptions = 'success', options?: { className?: string; position?: 'top-left' | 'top-right' | 'top-center' | 'bottom-left' | 'bottom-right' | 'bottom-center' }) => {
  toast(message, {
    position: options?.position || 'top-center',
    theme: 'colored',
    autoClose: 3500,
    type,
    className: `shadow-lg ${options?.className || ''}`.trim(),
    style: {
      maxWidth: '672px',
      ...(type === 'warning' ? { color: '#212121' } : {})
    },
    closeButton: type === 'warning' ? CustomCloseButton : undefined
  });
};

export const calculateRemainingChars = (text: string = '', limit: number = 255) => {
  if (typeof text !== 'string') return limit;
  return limit - text.length;
};

export const getPlatformImageURL = (platform: string): string => {
  switch (platform) {
    case 'ipa':
    case 'In-person':
      return '/images/in_person.svg';
    case 'hybrid':
    case 'Hybrid':
      return '/images/hybrid.svg';
    case 'other-online-events':
    case 'Other online platforms':
      return '/images/OtherOnlinePlatforms.svg';
    case 'zoom':
    case 'Zoom':
      return '/images/Zoom.svg';
    case 'meet':
    case 'Google Meet':
      return '/images/meet-icon.svg';
    case 'teams':
    case 'Microsoft Teams':
    case 'teams_web_bot':
      return '/images/teams-logo.svg';
    case 'youtube':
    case 'YouTube':
      return '/images/youtube-icon.svg';
    case 'twitch':
    case 'Twitch':
      return '/images/twitch-icon.svg';
    case 'vimeo':
    case 'Vimeo':
      return '/images/vimeo-icon.svg';
    case 'webex':
    case 'Webex':
      return '/images/webex-icon.svg';
    case 'linkedin':
    case 'LinkedIn':
      return '/images/linkedin-icon.svg';
    case 'restream':
    case 'Restream':
      return '/images/restream.svg';
    case 'streamyard':
    case 'Streamyard':
      return '/images/streamyard.png';
    case 'airmeet':
    case 'Airmeet':
      return '/images/airmeet.svg';
    case 'kick':
    case 'Kick':
      return '/images/kick.png';
    case 'adobe_connect':
    case 'Adobe Connect':
      return '/images/adobe-connect-logo.svg';
    default:
      return '';
  }
};

export const getDefaultTitle = (interactionType: InteractionType) => {
  switch (interactionType) {
    case 'newWheel':
      return 'And the winner is ...';
    case 'linkLibrary':
      return 'StreamAlive found these links in the chat...';
    case 'Quick Questions':
      return 'StreamAlive found these questions in the chat...';
    case 'textTrack':
      return 'Text from the chat...';
    case 'fantasticFans':
      return 'Your top commenters are...';
    case 'credits':
      return 'Roll the end credits...';
    case 'ratingPolls':
      return 'What would you rate this session?';
    default:
      return '';
  }
};

export const getCardImage = (question?: Interaction): string => {
  if (!question) return '';
  const imgPath = '/images/ros/template';
  const imgMap: Record<string, string> = {
    pollWithOptions: `${imgPath}/powerpolls.svg`,
    freeForm: `${imgPath}/powerpolls.svg`,
    map: `${imgPath}/map.svg`,
    wordCloud: `${imgPath}/wordcloud.svg`,
    wheel: `${imgPath}/choice.svg`,
    emojisEverywhere: `${imgPath}/emoji-list.svg`,
    talkingTiles: `${imgPath}/talking-tiles.svg`,
    transientThought: `${imgPath}/transient-thoughts.svg`,
    newWheel: `${imgPath}/ww.svg`,
    linkLibrary: `${imgPath}/link.svg`,
    'Quick Questions': `${imgPath}/qq.svg`,
    textTrack: `${imgPath}/text-track.svg`,
    'PDF Slide': getSlideUrl({
      interaction: question,
      isLargePreview: true,
      slide: undefined,
    }),
    ratingPolls: `${imgPath}/rating-polls.svg`,
    fantasticFans: `${imgPath}/fantastic-fans.svg`,
    multipleChoice: `${imgPath}/multiple-chouce.svg`,
    quiz: `${imgPath}/quiz.svg`,
  };

  const type = question.type as keyof typeof imgMap;
  return imgMap[type] || '';
};

export const getDefaultOptions = (interaction_type: InteractionType) => {
  switch (interaction_type) {
    case 'pollWithOptions':
      return [
        { id: v4(), body: 'Option 1' },
        { id: v4(), body: 'Option 2' },
      ];

    case 'wheel':
      return [
        { id: v4(), body: 'Option 1' },
        { id: v4(), body: 'Option 2' },
      ];
    case 'newWheel':
      return [
        { id: v4(), body: 'Liam' },
        { id: v4(), body: 'Olivia' },
        { id: v4(), body: 'Emma' },
      ];
    case 'ratingPolls':
      return [
        { id: v4(), body: '1', option_number: 1 },
        { id: v4(), body: '2', option_number: 2 },
        { id: v4(), body: '3', option_number: 3 },
        { id: v4(), body: '4', option_number: 4 },
        { id: v4(), body: '5', option_number: 5 },
      ];
    case 'multipleChoice':
      return [
        { id: v4(), body: 'Option 1', checked: false, },
        { id: v4(), body: 'Option 2', checked: false, },
      ];
    case 'quiz':
      return [
        { id: v4(), body: 'Option 1', checked: true, },
        { id: v4(), body: 'Option 2', checked: false, },
        { id: v4(), body: 'Option 3', checked: false, },
        { id: v4(), body: 'Option 4', checked: false, },
      ];
    default:
      return [];
  }
};
